import { showTransactionEntryDetailsModal } from '@/components/cashflow/transactions/TransactionEntryDetailsModal';
import { useDrawer } from '@/components/common/DrawerManager';
import { useAccountSetupSteps } from '@/components/goals/useAccountSetupSteps';
import { useIdvPosition } from '@/components/idv/useIdvPosition';
import usePlaid from '@/components/plaid/usePlaid';
import { useTracking } from '@/services/analytics/useTracking';
import { useShowInvitePartner } from '@common/react/partnerships';
import { useCurrentUser } from '@common/react/user';
import { isUndefined } from '@common/util/types';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { showPaymentModal } from './billing/PaymentModal';
import { startFinancialEntityAddFlow } from './financialEntities/FinancialEntityAddFlow';
import { showInviteModal } from './partnershipInvites/InviteModal';

type ModalHandler = {
    action: () => void;
    condition?: () => boolean;
    requiredParams?: string[];
};

const ModalFromUrlQueryManager = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const router = useRouter();
    const { showDrawer } = useDrawer();
    const showInvitePartner = useShowInvitePartner();
    const accountLinking = usePlaid();
    const { membership } = useCurrentUser();
    const { handleNextStep: openIdvModal } = useAccountSetupSteps({});
    const idvPosition = useIdvPosition();
    const { track } = useTracking();

    // Keep track of which modal was shown to prevent showing it again when query params update
    const handledModals = useRef(new Set<string>());

    // The key in this object corresponds to the `modal` query param
    const modalHandlers: Record<string, ModalHandler> = {
        addAccount: {
            action: () => {
                startFinancialEntityAddFlow({
                    onCompleteManual: (feId) => {
                        showDrawer({
                            drawerKey: 'financial-entity-detail',
                            entityId: feId,
                        });
                    },
                });
            },
        },
        billing: {
            condition: () => !!membership,
            action: () => {
                showPaymentModal({
                    includeTrial: membership?.status === 'TRIALING',
                });
            },
        },
        cashGoal: {
            condition: () => router.pathname === '/investing',
            action: () =>
                showDrawer({
                    drawerKey: 'goal-detail',
                    entityId: 'CASH_MANAGEMENT',
                }),
        },
        connectAccount: {
            action: () => accountLinking.open(),
        },
        idv: {
            condition: () => idvPosition !== 'complete',
            action: () => openIdvModal({}),
        },
        investingGoal: {
            condition: () => router.pathname === '/investing',
            action: () =>
                showDrawer({
                    drawerKey: 'goal-detail',
                    entityId: 'GENERAL_INVESTING',
                }),
        },
        invitePartner: {
            condition: () => showInvitePartner,
            action: () => showInviteModal(),
        },
        transactionEntryDetail: {
            action: () => {
                showTransactionEntryDetailsModal({
                    transactionEntryId: router.query.id as string,
                    showDrawer,
                });
            },
            requiredParams: ['id'],
        },
    };

    useEffect(() => {
        const modalType = router.query.modal;
        if (!modalType || Array.isArray(modalType)) return;

        if (handledModals.current.has(modalType)) return;

        const handler = modalHandlers[modalType];
        if (!handler) return;

        const hasRequiredParams =
            handler.requiredParams?.every(
                (param) =>
                    router.query[param] && !Array.isArray(router.query[param])
            ) ?? true;

        const passesCondition =
            isUndefined(handler.condition) || handler.condition();

        if (hasRequiredParams && passesCondition) {
            handler.action();
            handledModals.current.add(modalType);
        }

        track('Modal Opened From URL', {
            modal_type: modalType,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query, router.pathname]);

    return <>{children}</>;
};

export default ModalFromUrlQueryManager;
